import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Copy, Check, ChevronLeft, ChevronDown, ChevronRight, Cpu, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";


const MCP_FUNCTIONS = [
  { name: "search_posts", description: "Fetch saved posts with filters (platform, date, category, mood)" },
  { name: "list_categories", description: "List all your content categories" },
  { name: "get_post_detail", description: "Get full details of a specific post" },
  { name: "get_total_posts_count", description: "Get total number of saved posts" },
  { name: "get_platform_breakdown", description: "Get post counts broken down by platform" },
  { name: "search_by_mood", description: "Search posts by mood or emotional context" },
  { name: "get_image_text_content", description: "Extract text and OCR content from post images" },
  { name: "compare_posts", description: "Compare multiple posts side by side" },
];

const SERVER_URL = "https://mcp.instavault.co/sse";

type GuideKey = "connector" | "chatgpt" | "cli" | "other";

// ── MCP SVG icon ─────────────────────────────────────────────────────────────

const McpIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" fillRule="evenodd" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z" />
    <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z" />
  </svg>
);

const ClaudeIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fb923c" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="2" x2="12" y2="22" />
    <line x1="20.66" y1="7" x2="3.34" y2="17" />
    <line x1="20.66" y1="17" x2="3.34" y2="7" />
  </svg>
);

const ChatGPTIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#10a37f" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.28 9.28a5.76 5.76 0 00-.52-4.73 5.83 5.83 0 00-6.27-2.8A5.76 5.76 0 0011.24 0a5.83 5.83 0 00-5.56 4.04 5.76 5.76 0 00-3.84 2.79 5.83 5.83 0 00.72 6.84 5.76 5.76 0 00.52 4.73 5.83 5.83 0 006.27 2.8A5.76 5.76 0 0012.76 24a5.83 5.83 0 005.57-4.04 5.76 5.76 0 003.84-2.79 5.83 5.83 0 00-.89-6.89zM12.76 22.4a4.32 4.32 0 01-2.77-1c.03-.02.09-.05.13-.07l4.6-2.66a.75.75 0 00.38-.65v-6.5l1.94 1.12a.07.07 0 01.04.05v5.38a4.33 4.33 0 01-4.32 4.33zm-9.3-3.97a4.3 4.3 0 01-.52-2.9c.03.02.09.06.13.08l4.6 2.66a.76.76 0 00.76 0l5.62-3.24v2.24a.07.07 0 01-.03.06L9.4 20.08a4.33 4.33 0 01-5.94-1.65zm-1.21-9.47a4.3 4.3 0 012.25-1.9v5.47a.75.75 0 00.38.65l5.61 3.24-1.95 1.12a.07.07 0 01-.07 0L4.2 15.1a4.33 4.33 0 01-.95-6.14zm15.93 3.72L12.57 9.4l1.95-1.12a.07.07 0 01.07 0l5.27 3.04a4.32 4.32 0 01-.67 7.8v-5.47a.75.75 0 00-.41-.64zm1.94-2.92a6.55 6.55 0 00-.13-.08l-4.59-2.65a.76.76 0 00-.76 0L9.02 10.28V8.04a.07.07 0 01.03-.06l5.27-3.04a4.33 4.33 0 016.8 4.82zm-12.17 4L6.02 12.64a.07.07 0 01-.04-.05V7.21a4.33 4.33 0 017.1-3.32 5.32 5.32 0 00-.13.07l-4.6 2.66a.75.75 0 00-.38.65l-.02 6.49zm1.06-2.27l2.5-1.44 2.5 1.44v2.88l-2.5 1.44-2.5-1.44V11.5z" />
  </svg>
);

// ── Collapsible wrapper ───────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, isOpen, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-700/40 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded flex items-center justify-center shrink-0">{icon}</div>
          <span className="text-white font-medium text-sm">{title}</span>
        </div>
        {isOpen
          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-700 bg-gray-800/60 rounded-b-lg">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Code block ────────────────────────────────────────────────────────────────

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative rounded-lg border border-gray-700 bg-gray-900">
      <button
        onClick={handleCopy}
        className="absolute top-2.5 right-2.5 p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
        aria-label="Copy"
      >
        {copied
          ? <Check className="w-3.5 h-3.5 text-green-400" />
          : <Copy className="w-3.5 h-3.5 text-gray-400" />}
      </button>
      <pre className="p-4 pr-12 text-xs text-gray-300 overflow-x-auto leading-relaxed font-mono whitespace-pre">
        {code}
      </pre>
    </div>
  );
}

// ── Copyable field row ────────────────────────────────────────────────────────

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5">
      <span className="text-gray-500 text-xs w-24 shrink-0">{label}</span>
      <code className="text-green-400 text-xs font-mono flex-1 truncate leading-relaxed cursor-default select-all">
        {value}
      </code>
      <button
        onClick={handleCopy}
        className="shrink-0 p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
        aria-label={`Copy ${label}`}
      >
        {copied
          ? <Check className="w-3.5 h-3.5 text-green-400" />
          : <Copy className="w-3.5 h-3.5 text-gray-400" />}
      </button>
    </div>
  );
}

// ── Step list ─────────────────────────────────────────────────────────────────

function Steps({ steps }: { steps: { label: string; detail: string }[] }) {
  return (
    <div className="space-y-2">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-5 h-5 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-orange-300 text-xs font-semibold">{i + 1}</span>
          </div>
          <div>
            <span className="text-white text-xs font-medium">{step.label}</span>
            <p className="text-gray-500 text-xs mt-0.5">{step.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function McpContent({ userInfo }: { userInfo?: { id: string } }) {
  const [showTools, setShowTools] = useState(false);
  const [openGuide, setOpenGuide] = useState<GuideKey | null>(null);
  const navigate = useNavigate();


  const toggleGuide = (key: GuideKey) =>
    setOpenGuide((prev) => (prev === key ? null : key));

  const cliCommand =
    `claude mcp add \\\n` +
    `  --transport sse instavault \\\n` +
    `  ${SERVER_URL}`;

  const otherConfig = JSON.stringify(
    {
      mcpServers: {
        instavault: {
          command: "npx",
          args: ["-y", "mcp-remote", SERVER_URL],
        },
      },
    },
    null, 2
  );

  // ── Authorised view ───────────────────────────────────────────────────────

  return (
    <div className="space-y-5 max-w-3xl">
      <button
        onClick={() => navigate("/profile?active=integrations")}
        className="flex items-center gap-2 bg-transparent text-gray-400 hover:text-white transition-colors text-sm"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Integrations
      </button>

      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gray-800 border border-gray-700 rounded-xl flex items-center justify-center">
            <McpIcon className="w-5 h-5 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">MCP Integration</h2>
          <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs">
            Beta
          </Badge>
        </div>
        <p className="text-gray-400">
          Connect your Instavault data to any MCP-compatible AI — Claude, Claude Code, and more.
        </p>
      </div>

      {/* Connection info */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <McpIcon className="w-4 h-4 text-purple-400" />
            Connect via OAuth 2.0
          </CardTitle>
          <CardDescription className="text-gray-400">
            Instavault uses OAuth 2.0 for secure MCP access — no credentials to manage.
            Your AI client will open a browser to sign you in automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CopyField label="Server URL" value={SERVER_URL} />

          <div className="pt-3 border-t border-gray-700 space-y-3">
            <p className="text-gray-400 text-sm font-medium">Add to your AI tool</p>

            <CollapsibleSection
              title="Claude Connector (claude.ai)"
              icon={<ClaudeIcon />}
              isOpen={openGuide === "connector"}
              onToggle={() => toggleGuide("connector")}
            >
              <div className="space-y-4 pt-3">
                <Steps steps={[
                  { label: "Open Claude.ai", detail: "Go to claude.ai in your browser" },
                  { label: "Settings → Customize", detail: "Click your profile → Settings → Customize" },
                  { label: "Connectors → +", detail: "Open the Connectors tab and click the + button" },
                  { label: "Add Custom Connector", detail: 'Select "Add Custom Connector" from the menu' },
                  { label: "Paste the Server URL", detail: "Enter the URL below and save — you'll be prompted to sign in" },
                ]} />
                <CopyField label="Server URL" value={SERVER_URL} />
                <p className="text-gray-500 text-xs">
                  Claude will open a browser window asking you to sign in to your InstaVault account.
                </p>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="ChatGPT"
              icon={<ChatGPTIcon />}
              isOpen={openGuide === "chatgpt"}
              onToggle={() => toggleGuide("chatgpt")}
            >
              <div className="space-y-4 pt-3">
                <Steps steps={[
                  { label: "Open ChatGPT", detail: "Go to chatgpt.com in your browser" },
                  { label: "Profile → Settings", detail: "Click your profile icon at the bottom-left, then Settings" },
                  { label: "Apps", detail: "Click the Apps tab" },
                  { label: "Create App", detail: 'Click "Create App" — enable Developer Mode if prompted' },
                  { label: "Add remote URL", detail: "Paste the URL below, give it a name, and save" },
                ]} />
                <CopyField label="Server URL" value={SERVER_URL} />
                <p className="text-gray-500 text-xs">
                  ChatGPT will open a browser window asking you to sign in to your InstaVault account.
                </p>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Claude Code (CLI)"
              icon={<ClaudeIcon />}
              isOpen={openGuide === "cli"}
              onToggle={() => toggleGuide("cli")}
            >
              <div className="space-y-3 pt-3">
                <p className="text-gray-400 text-xs">Run this in your terminal — Claude Code will open a browser to authenticate:</p>
                <CodeBlock code={cliCommand} />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Other LLMs (mcp-remote)"
              icon={<Cpu className="w-4 h-4 text-purple-400" />}
              isOpen={openGuide === "other"}
              onToggle={() => toggleGuide("other")}
            >
              <div className="space-y-3 pt-3">
                <p className="text-gray-400 text-xs">
                  Paste into your LLM's MCP configuration file. <code className="text-gray-300">mcp-remote</code> handles OAuth automatically.
                </p>
                <CodeBlock code={otherConfig} />
                <p className="text-gray-500 text-xs">
                  Requires <code className="text-gray-400">npx</code> and{" "}
                  <code className="text-gray-400">mcp-remote</code> (auto-installed).
                  A browser window will open for sign-in on first use.
                </p>
              </div>
            </CollapsibleSection>
          </div>
        </CardContent>
      </Card>

      {/* Available tools */}
      <div className="rounded-lg border border-gray-700 bg-gray-800">
        <button
          onClick={() => setShowTools((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-700/40 transition-colors rounded-lg"
        >
          <div>
            <span className="text-white font-medium text-sm">Available Tools</span>
            <p className="text-gray-400 text-xs mt-0.5">
              {MCP_FUNCTIONS.length} functions accessible to any MCP-compatible client
            </p>
          </div>
          {showTools
            ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
        </button>
        {showTools && (
          <div className="px-4 pb-4 pt-1 border-t border-gray-700 bg-gray-800/60 rounded-b-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {MCP_FUNCTIONS.map((fn) => (
                <div key={fn.name} className="p-3 rounded-lg bg-gray-700/50 border border-gray-600">
                  <code className="text-purple-300 text-xs font-mono block mb-1">{fn.name}</code>
                  <span className="text-gray-400 text-xs">{fn.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
